import type { MessageKey } from "@wato/i18n";
import {
  channelState,
  isChannelInactive,
  withoutStalePlaceholders,
  type ChannelState,
} from "@wato/platform/channel-visibility";
import type { ChannelSummary } from "../../api/types";
import { formatPhone } from "../../lib/format-phone";

/**
 * FB3-14: the single status source for the top cards and the number lists, so they can never
 * contradict each other. The rule itself (state + stale-row hiding) lives in
 * `@wato/platform/channel-visibility`, shared with the owner overview.
 */
export { channelState, isChannelInactive, withoutStalePlaceholders, type ChannelState };

/** Number first (Graph / WAHA), then the name; never the provider's raw id. */
export function channelNumber(c: ChannelSummary): string | null {
  return c.displayPhoneNumber || null;
}

export function channelTitle(c: ChannelSummary): string | null {
  // The owner's own name (settable via rename) wins over Meta's verified name.
  return c.displayName || c.verifiedName || null;
}

/** Best human label: name, else the formatted number; null only when both are missing. */
export function channelLabel(c: ChannelSummary): string | null {
  return channelTitle(c) || channelNumber(c);
}

/** Fallback wording per provider, passed in by the caller (already translated). */
export interface ChannelFallbackLabels {
  /** WhatsApp number with no name / phone on record. */
  readonly unnamed: string;
  /** Instagram account with no name and no stored @username. */
  readonly instagram: string;
  /** Messenger (Facebook Page) with no name and no stored page name. */
  readonly messenger: string;
  /** TikTok account with no name. */
  readonly tiktok: string;
}

/** Builds the translated fallback wording; `unnamedKey` lets a screen keep its own WhatsApp text. */
export function channelFallbackLabels(
  t: (key: MessageKey) => string,
  unnamedKey: MessageKey = "channels.multiNumber.unnamed",
): ChannelFallbackLabels {
  return {
    unnamed: t(unnamedKey),
    instagram: t("channels.multiNumber.unnamedInstagram"),
    messenger: t("channels.multiNumber.unnamedMessenger"),
    tiktok: t("channels.multiNumber.unnamedTiktok"),
  };
}

/**
 * Q4-CHN-03: label for a channel with neither a name nor a phone on record. WhatsApp: the
 * «unnamed» text plus the last digits of the provider reference (two such numbers stay
 * distinguishable). Instagram / Messenger: the stored handle (`@username` / page name), else a
 * generic account label. Never the provider reference for non-WhatsApp channels.
 */
export function channelLabelOr(c: ChannelSummary, labels: ChannelFallbackLabels): string {
  const label = channelLabel(c);
  if (label) return label;
  if (c.provider === "instagram") return c.displayHandle ? `@${c.displayHandle}` : labels.instagram;
  if (c.provider === "messenger") return c.displayHandle || labels.messenger;
  if (c.provider === "tiktok") return labels.tiktok;
  const digits = (c.externalRef ?? "").replace(/\D/g, "");
  return digits.length >= 4 ? `${labels.unnamed} ••${digits.slice(-4)}` : labels.unnamed;
}

/** Connected number: the stored phone, else a provider ref that is itself a phone (never a session id). */
function channelPhone(c: ChannelSummary): string {
  if (c.displayPhoneNumber) return formatPhone(c.displayPhoneNumber);
  // OWN-372: a Cloud API reference is Meta's numeric phone-number id, never a phone.
  if (c.provider === "whatsapp_cloud") return "";
  const ref = (c.externalRef ?? "").split("@")[0] ?? "";
  return /^\+?\d{8,15}$/.test(ref) ? formatPhone(ref) : "";
}

/**
 * REV-01: the one friendly channel name used by every filter, table and picker: the owner's name
 * (+ formatted number), else «kind - number» (e.g. «QR - +973 3827 3614»), else the kind label,
 * else the caller's «unnamed» text. Never a raw id.
 */
export function channelDisplayName(
  c: ChannelSummary,
  labels: { readonly kind: string; readonly unnamed: string },
): string {
  const name = channelTitle(c) ?? "";
  const phone = channelPhone(c);
  if (name) return [name, phone].filter(Boolean).join(" ");
  if (phone) return labels.kind ? `${labels.kind} - ${phone}` : phone;
  return labels.kind || labels.unnamed;
}

/** REV-02: channels shown in selectors; disconnected/migrated ones only on request. */
export function selectableChannels<T extends ChannelSummary>(
  channels: readonly T[],
  showDisconnected: boolean,
): T[] {
  return channels.filter((c) => showDisconnected || !isChannelInactive(c));
}

/** Meta's sandbox WABA number ships with this verified name. */
const META_TEST_NAME = "test number";

/** Leading glyph per kind: a native <option> cannot hold an <Icon>. */
export const CHANNEL_KIND_ICON: Readonly<Record<ChannelOption["kind"], string>> = {
  official: "🟢",
  qr: "📷",
};

/** Digits of the connected number (stored phone, else a phone-shaped reference); "" when none. */
export function channelDigits(c: ChannelSummary): string {
  return channelPhone(c).replace(/\D/g, "");
}

export interface ChannelOption {
  /** Official WhatsApp (Cloud API) or a QR session; picker rows show a matching icon. */
  readonly kind: "official" | "qr";
  readonly label: string;
}

/**
 * OWN-368: one readable picker row per sender number: the owner's name, else the formatted phone,
 * «test number · phone» for Meta's sandbox name. Never the provider reference or its last digits.
 */
export function channelOptionLabel(
  c: ChannelSummary,
  t: (key: MessageKey, params?: Record<string, string>) => string,
): ChannelOption {
  const kind = c.provider === "whatsapp_cloud" ? "official" : "qr";
  const title = channelTitle(c);
  const phone = channelPhone(c);
  if (title && title.trim().toLowerCase() === META_TEST_NAME) {
    return {
      kind,
      label: phone ? t("channels.b29b.testNumberWith", { phone }) : t("channels.b29b.testNumber"),
    };
  }
  return { kind, label: title || phone || t("channels.multiNumber.unnamed") };
}
